import { useCallback, useEffect, useRef, useState } from "react";
import { Center, Loader, Skeleton, Stack, Text, Title } from "@mantine/core";
import { useIntersection } from "@mantine/hooks";
import { useNavigate } from "react-router-dom";
import { notifications } from "@mantine/notifications";
import { FeedCard } from "../components/FeedCard";
import { FilterPanel } from "../components/FilterPanel";
import { useFilters } from "../hooks/useFilters";
import { api } from "../lib/api";

interface FeedItem {
  changeId: string;
  productId: string;
  productName: string;
  productLineName: string | null;
  brandName: string;
  brandDomain: string | null;
  retailerId: string;
  retailerName: string;
  retailerDomain: string | null;
  changeType: string;
  previousPrice: string | null;
  newPrice: string;
  priceDelta: string | null;
  priceDeltaPct: string | null;
  detectedAt: string;
  productUrl: string | null;
  imageUrl: string | null;
}

interface FeedResponse {
  items: FeedItem[];
  nextCursor: string | null;
  hasMore: boolean;
}

export function FeedPage() {
  const navigate = useNavigate();
  const filterCtx = useFilters();
  const [items, setItems] = useState<FeedItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  // RC-004 fix: AbortController replaces loadingRef gate. When filters change,
  // the previous in-flight request is aborted, preventing stale data from
  // overwriting state. Matches the pattern used in GridPage/HistoryPage.
  const controllerRef = useRef<AbortController | null>(null);

  const fetchFeed = useCallback(
    async (nextCursor: string | null, append: boolean) => {
      // Abort any previous in-flight request
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;

      try {
        const params: Record<string, unknown> = {
          limit: 20,
          ...filterCtx.toApiParams(),
        };
        if (nextCursor) params.cursor = nextCursor;

        const { data } = await api.get<FeedResponse>("/feed", {
          params,
          signal: controller.signal,
        });

        if (controller.signal.aborted) return;
        setItems((prev) => (append ? [...prev, ...data.items] : data.items));
        setCursor(data.nextCursor);
        setHasMore(data.hasMore);
      } catch (err) {
        if (controller.signal.aborted) return;
        notifications.show({
          title: "Failed to load feed",
          message: err instanceof Error ? err.message : "Network error",
          color: "red",
        });
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterCtx.toApiParams],
  );

  // Fetch on mount and when filters change
  useEffect(() => {
    setItems([]);
    setCursor(null);
    setHasMore(true);
    setLoading(true);
    fetchFeed(null, false);
    return () => controllerRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCtx.filterKey]);

  // Infinite scroll
  const { ref: sentinelRef, entry } = useIntersection({ threshold: 0.1 });

  useEffect(() => {
    if (entry?.isIntersecting && hasMore && !loadingMore) {
      setLoadingMore(true);
      fetchFeed(cursor, true);
    }
  }, [entry?.isIntersecting, hasMore, loadingMore, cursor, fetchFeed]);

  if (loading) {
    return (
      <Stack gap="md">
        <Title order={2}>Price Change Feed</Title>
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} height={100} radius="sm" />
        ))}
      </Stack>
    );
  }

  if (items.length === 0) {
    return (
      <Stack gap="md">
        <Title order={2}>Price Change Feed</Title>
        <Center py={60}>
          <Text c="dimmed">
            No price changes detected yet. Your scrapers are running and will
            populate this feed soon.
          </Text>
        </Center>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Title order={2}>Price Change Feed</Title>
      <FilterPanel filters={filterCtx} />

      {items.map((item) => (
        <FeedCard
          key={item.changeId}
          {...item}
          onViewHistory={() => navigate(`/history/${item.productId}`)}
        />
      ))}

      {/* Infinite scroll sentinel */}
      <div ref={sentinelRef} style={{ height: 1 }} />

      {loadingMore && (
        <Center py="md">
          <Loader size="sm" />
        </Center>
      )}

      {!hasMore && items.length > 0 && (
        <Center py="md">
          <Text size="sm" c="dimmed">
            No more price changes
          </Text>
        </Center>
      )}
    </Stack>
  );
}
